// src/features/customers/components/BenachrichtigungszentrumSchalter.tsx
//
// Schalter je Kunde: E-Mails über das Benachrichtigungszentrum des
// Kundenportals (06.10.2026, Freigabe Tim). Steht im Reiter "Versand &
// Sichtbarkeit" und wirkt sofort wie die übrige Übersicht dort.
//
// An:  Paketfreigaben, Rechnungen, Stammdatenbestätigungen und die Mail aus
//      der Finalisierung gehen nicht mehr direkt raus, sondern über das
//      Benachrichtigungszentrum an die Portal-Ansprechpartner nach deren
//      Einstellungen. Gemailt wird erst, was ab dem Einschalten entsteht.
// Aus: alles wie bisher.
//
// Regeln: Nur bei den Versandarten Kundenportal und kein Versand möglich,
// weder mit "E-Mail" noch mit DUO kombinierbar (Vorgabe Tim, auch in der
// Datenbank gesichert). Warnung, wenn eine Adresse aus "E-Mail" oder
// "E-Mail CC" der Kundendaten keinem aktiven Ansprechpartner gehört, denn bei
// eingeschaltetem Schalter erreichen die Mails nur Ansprechpartner.

import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/services/supabase';

interface Props {
  customerId: string;
  onUpdate?: () => void;
}

interface Stand {
  aktiv: boolean;
  seit: string | null;
  versandmethode: string | null;
  ohneAnsprechpartner: string[];
}

// Versandarten, mit denen der Schalter kombinierbar ist
const ERLAUBTE_VERSANDARTEN = ['xtax_portal', 'kein_versand'];

function adressen(text: string | null | undefined): string[] {
  return String(text || '')
    .split(/[,;\s]+/)
    .map((a) => a.trim().toLowerCase())
    .filter((a) => a.includes('@'));
}

export const BenachrichtigungszentrumSchalter: React.FC<Props> = ({ customerId, onUpdate }) => {
  const [stand, setStand] = useState<Stand | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    const [kundeAntwort, kontakteAntwort] = await Promise.all([
      supabase
        .from('customers')
        .select('benachrichtigungszentrum_aktiv, benachrichtigungszentrum_seit, versandmethode, email, email_cc')
        .eq('id', customerId)
        .single(),
      supabase
        .from('customer_contacts')
        .select('email')
        .eq('customer_id', customerId)
        .eq('is_active', true)
    ]);
    if (kundeAntwort.error || !kundeAntwort.data) {
      setFehler('Der Schalter konnte nicht geladen werden.');
      return;
    }
    const k = kundeAntwort.data as {
      benachrichtigungszentrum_aktiv: boolean;
      benachrichtigungszentrum_seit: string | null;
      versandmethode: string | null;
      email: string | null;
      email_cc: string | null;
    };
    const ansprechpartner = new Set(
      ((kontakteAntwort.data || []) as Array<{ email: string | null }>)
        .map((z) => String(z.email || '').trim().toLowerCase())
        .filter(Boolean)
    );
    const kundenAdressen = Array.from(new Set([...adressen(k.email), ...adressen(k.email_cc)]));
    setStand({
      aktiv: k.benachrichtigungszentrum_aktiv === true,
      seit: k.benachrichtigungszentrum_seit,
      versandmethode: k.versandmethode,
      ohneAnsprechpartner: kundenAdressen.filter((a) => !ansprechpartner.has(a))
    });
  }, [customerId]);

  useEffect(() => { laden(); }, [laden]);

  const umschalten = async () => {
    if (!stand) return;
    const neu = !stand.aktiv;
    if (neu && !ERLAUBTE_VERSANDARTEN.includes(stand.versandmethode || '')) return;
    setSpeichert(true);
    setFehler(null);
    const { error } = await supabase
      .from('customers')
      .update({ benachrichtigungszentrum_aktiv: neu })
      .eq('id', customerId);
    setSpeichert(false);
    if (error) {
      setFehler('Der Schalter konnte nicht gespeichert werden. Er ist nur bei den Versandarten Kundenportal und kein Versand möglich.');
      return;
    }
    await laden();
    if (onUpdate) onUpdate();
  };

  if (!stand) {
    return fehler
      ? <div style={{ marginBottom: '20px', color: '#DC2626', fontSize: '14px' }}>{fehler}</div>
      : null;
  }

  const gesperrt = !ERLAUBTE_VERSANDARTEN.includes(stand.versandmethode || '');
  const seitText = stand.seit
    ? new Date(stand.seit).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div style={{
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '20px',
      backgroundColor: '#FFFFFF'
    }}>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: gesperrt ? 'not-allowed' : 'pointer' }}>
        <input
          type="checkbox"
          checked={stand.aktiv}
          disabled={speichert || (gesperrt && !stand.aktiv)}
          onChange={umschalten}
          style={{ width: '18px', height: '18px', marginTop: '2px', cursor: 'inherit' }}
        />
        <span>
          <span style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827' }}>
            E-Mails über das Kundenportal (Benachrichtigungszentrum)
          </span>
          <span style={{ display: 'block', fontSize: '13px', color: '#6B7280', marginTop: '4px', lineHeight: 1.5 }}>
            Paketfreigaben, Rechnungen, Stammdatenbestätigungen und die Mail aus der Finalisierung gehen dann
            nicht mehr direkt raus, sondern über das Benachrichtigungszentrum an die Portal-Ansprechpartner,
            nach deren eigenen Einstellungen. Gemailt wird erst, was ab dem Einschalten entsteht.
          </span>
          {stand.aktiv && seitText && (
            <span style={{ display: 'block', fontSize: '13px', color: '#059669', marginTop: '6px' }}>
              Aktiv seit {seitText}
            </span>
          )}
        </span>
      </label>

      {gesperrt && (
        <div style={{ marginTop: '12px', padding: '10px 12px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '13px', color: '#374151' }}>
          Nur möglich bei den Versandarten „XTAX-Portal“ und „Kein Versand“. Mit E-Mail und DUO ist das
          Kundenportal nicht kombinierbar; bitte zuerst im Reiter Stammdaten die Versandart ändern.
        </div>
      )}

      {stand.aktiv && (
        <div style={{ marginTop: '12px', fontSize: '13px', color: '#374151' }}>
          Solange der Schalter an ist, können die Versandarten „E-Mail“ und „DUO“ nicht gewählt werden.
        </div>
      )}

      {stand.ohneAnsprechpartner.length > 0 && (
        <div style={{ marginTop: '12px', padding: '10px 12px', backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '6px', fontSize: '13px', color: '#92400E', lineHeight: 1.5 }}>
          Diese Adressen aus den Kundendaten gehören keinem aktiven Ansprechpartner:{' '}
          <strong>{stand.ohneAnsprechpartner.join(', ')}</strong>. Bei eingeschaltetem Schalter erhalten sie keine
          Mails mehr. Bitte als Ansprechpartner anlegen oder den Schalter aus lassen.
        </div>
      )}

      {fehler && <div style={{ marginTop: '12px', color: '#DC2626', fontSize: '13px' }}>{fehler}</div>}
    </div>
  );
};
